/**
 * Popover utility hook that uses Popper.js to return positioning styles that
 * adjust to fit within the viewport.
 * @see https://popper.js.org/
 */
import {useEffect, useMemo, useState} from 'react';
import {createPopper} from '@popperjs/core';

export default function usePopper({
  modifiers,
  onFirstUpdate,
  placement = 'bottom',
  popperNode,
  strategy = 'absolute',
  targetNode,
} = {}) {
  // Keeping track of Popper instance to return as part of the hook
  const [popper, setPopper] = useState(null);
  // Using state to track arrow ref in order to force an update to add the
  // arrow modifier
  const [arrow, setArrow] = useState(null);
  // The styles and attributes computed by Popper.js
  const [state, setState] = useState({
    styles: {
      popper: {
        position: 'absolute',
        left: '0',
        top: '0',
        // We hide the tooltip initially to prevent it flashing in this initial
        // spot while popper calculates where to place it
        visibility: 'hidden',
      },
    },
    attributes: {},
  });

  /**
   * Memoizing this since this should rarely ever change while the effect below
   * fires every time the popper changes. For example, tooltips fire that effect
   * every time it hides/shows.
   */
  const memoModifiers = useMemo(() => {
    const mods = [
      ...(modifiers || []),
      // Modifier to update the hook state with the computed styles/attributes
      // from Popper. This replaces the applyStyles modifier that Popper uses
      // internally by default.
      {
        name: 'updateState',
        enabled: true,
        phase: 'write',
        fn({state: popperState}) {
          const nextState = Object.keys(popperState.elements).reduce(
            (acc, key) => {
              if (popperState.attributes[key]) {
                acc.attributes[key] = popperState.attributes[key];
              }
              if (popperState.styles[key]) {
                acc.styles[key] = popperState.styles[key];
              }
              return acc;
            },
            // Make the tooltip visible when it gets placed
            {attributes: {}, styles: {visibility: 'visible'}}
          );

          setState(nextState);
        },
        requires: ['computeStyles'],
      },
      // Prevent Popper from re-applying the styles
      {name: 'applyStyles', enabled: false},
    ];

    // If arrow is set, we assume that the consumer wants an arrow to be shown
    // and add the arrow modifier
    if (arrow) {
      mods.push({
        name: 'arrow',
        enabled: true,
        options: {
          element: arrow,
        },
      });
    }

    return mods;
  }, [arrow, modifiers]);

  const config = useMemo(
    () => ({
      modifiers: memoModifiers,
      onFirstUpdate,
      placement,
      strategy,
    }),
    [memoModifiers, onFirstUpdate, placement, strategy]
  );

  useEffect(() => {
    // Ignore if nodes are null or undefined (ie. waiting for refs to be set)
    if (!targetNode || !popperNode) {
      return;
    }

    const popperInstance = createPopper(targetNode, popperNode, config);

    setPopper(popperInstance);
    // We need the early return at the top of the hook to silence Popper.js error
    // that may appear while refs are still being set
    // eslint-disable-next-line consistent-return
    return () => {
      popperInstance.destroy();
      setPopper(null);
    };
  }, [targetNode, popperNode, config]);

  return {
    arrowProps: {
      ...state.attributes.arrow,
      ref: setArrow,
      style: state.styles.arrow,
    },
    popperProps: {
      ...state.attributes.popper,
      style: state.styles.popper,
    },
    instance: popper,
  };
}
